import { useLocale } from "@/i18n/LocaleProvider";
import { useEffect, useMemo, useState } from "react";
import {
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@/components/ui/sidebar";
import ProfilePage from "./Profile";
import CompanyPage from "./CompanyPage";
import NotificationAlert from "./NoificationAlertPage";
import { useAuth } from "../components/AuthContext";
import BrandPage from "./BrandPage";
import { Badge } from "@/components/ui/badge";
import { authAPI, paymentAPI } from "@/services/api";
import { useLocation, useNavigate, useOutletContext } from "react-router";
import { toast } from "sonner";
import { TriangleAlert } from "lucide-react";
import {
  isBrandMemberTier,
  isWorkspaceMemberTier,
} from "@/lib/memberTier";

const data = {
  plans: ["Trial", "Pro"],
  navMain: [
    {
      title: "Track",
      url: "#",
      items: [
        {
          title: "Company",
          url: "company",
        },
        {
          title: "Brand",
          url: "brand",
        },
        {
          title: "Account",
          url: "account",
          isActive: true,
        },
        {
          title: "Notifications",
          url: "notifications",
        },
        {
          title: "Billing",
          url: "billing",
          soon: true,
        },
      ],
    },
  ],
};

const settingsNavLabelKeys: Record<string, string> = {
  company: "settings.nav.company",
  brand: "settings.nav.brand",
  account: "settings.nav.account",
  notifications: "settings.nav.notifications",
  billing: "settings.nav.billing",
};

const Settings = () => {
  const { t } = useLocale();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { company, currentBrand } = useOutletContext<{
    company?: { role?: "company_admin" | "company_member" };
    currentBrand?: {
      accessRole?: "company_admin" | "company_member" | "brand_member";
    };
  }>();
  const activeAccessRole = company?.role || currentBrand?.accessRole;
  const isBrandMember = activeAccessRole
    ? activeAccessRole === "brand_member"
    : isBrandMemberTier(user?.tier);
  const isWorkspaceMember = activeAccessRole
    ? activeAccessRole !== "company_admin"
    : isWorkspaceMemberTier(user?.tier);
  const activeTab = location.pathname.split("/").pop();
  const searchParams = location.search || "";
  const visibleItems = useMemo(
    () =>
      data.navMain[0].items.filter((item) => {
        if (item.url === "company") return !isBrandMember;
        if (item.url === "billing") return !isWorkspaceMember;
        return true;
      }),
    [isBrandMember, isWorkspaceMember],
  );
  const currentTab = useMemo(
    () => visibleItems.find((item) => item.url === activeTab),
    [activeTab, visibleItems],
  );

  const [activeItem, setActiveItem] = useState<string>(
    currentTab?.url || visibleItems[0]?.url || "brand",
  );
  const [userData, setUserData] = useState<any>(null);

  useEffect(() => {
    const nextUrl = currentTab?.url || visibleItems[0]?.url || "brand";
    setActiveItem(nextUrl);
  }, [currentTab?.url, visibleItems]);

  useEffect(() => {
    if (!user?.brands.length || !user.company.name) {
      const getMe = async () => {
        try {
          const { brands, user, company } = await authAPI.getMe();
          setUserData({
            ...user,
            brands: brands || [],
            company: company || {},
          });
        } catch {
          setUserData(null);
        }
      };
      getMe();
    } else {
      setUserData({
        ...user,
        brands: user.brands || [],
        company: user.company || {},
      });
    }
  }, [user]);

  return (
    <div className="flex-col lg:flex-row flex h-full">
      <div className="w-3xs">
        <SidebarContent>
          {data.navMain.map((item) => (
            <SidebarGroup key={item.title}>
              <SidebarGroupContent>
                <SidebarMenu>
                  {visibleItems.map((item) => (
                    <SidebarMenuItem
                      key={item.title}
                      onClick={async () => {
                        if (!isWorkspaceMember && item.title === "Billing") {
                          try {
                            const portal = await paymentAPI.getCustomerPortal();
                            window.open(portal.url, "billingWindow");
                          } catch {
                            toast.error(
                              t("legacy.couldt.open.billing.gls920"),
                              {
                                icon: (
                                  <div className="w-6 h-6 bg-muted rounded-lg flex items-center justify-center">
                                    <TriangleAlert className="w-4 h-4 text-black" />
                                  </div>
                                ),
                                position: "bottom-right",
                              },
                            );
                          }
                        }
                        if (!item.soon) setActiveItem(item.url);
                      }}
                    >
                      <SidebarMenuButton
                        className={`${item.soon && item.title !== "Billing" && "active:text-gray-500 hover:text-gray-500 text-gray-500"}`}
                        asChild
                        onClick={() => {
                          if (!item.soon) {
                            setActiveItem(item.url);
                            navigate(
                              `/dashboard/settings/${item.url}${searchParams}`,
                            );
                          }
                        }}
                        isActive={activeItem === item.url && !item.soon}
                      >
                        <a className="cursor-pointer">
                          <p className="flex items-center gap-2">
                            {t(settingsNavLabelKeys[item.url] || item.title)}
                          </p>{" "}
                          {item.soon && (
                            <Badge
                              variant="secondary"
                              className={`${item.title === "Billing" && "border-[#635BFF] bg-white text-[#635BFF] font-semibold"}`}
                            >
                              {item.title === "Billing"
                                ? "stripe"
                                : t("common.comingSoon")}
                            </Badge>
                          )}
                        </a>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          ))}
        </SidebarContent>
      </div>
      <div className="border rounded-md w-full h-full">
        {activeItem === "account" && <ProfilePage user={userData} />}
        {!isBrandMember && activeItem === "company" && (
          <CompanyPage user={userData} />
        )}
        {activeItem === "brand" && <BrandPage />}
        {activeItem === "notifications" && <NotificationAlert />}
      </div>
    </div>
  );
};

export default Settings;
